import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import {
  CloseOutlined,
  AddOutlined,
  EditOutlined,
  StarOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  fetchTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  addTeamMember,
  removeTeamMember,
  updateTeamLocations,
  removeTeamLocation,
} from "../../store/workOrdersSlice";
import { getUsers } from "../../../settings/customerUsersSlice";
import { RootState } from "../../../../core/store";
import { User, UserState } from "../../../settings/models/userModels";
import { Team, TeamLocation, Location } from "../../models/models";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../../shared/themes/redesignTokens";
import { FormTextField, FormTextarea, textFieldSx } from "../../../../shared/components/redesign/forms";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import api from "../../../../core/api";
import teamsApi from "../../api/teamsApi";

// ─── Distinct avatar/progress bar colors per member index ───
const MEMBER_COLORS = [
  "#E67E22", // orange
  "#3498DB", // blue
  "#27AE60", // green
  "#8E44AD", // purple
  "#E74C3C", // red
  "#1ABC9C", // teal
  "#F39C12", // amber
  "#2980B9", // dark blue
];

// ─── Distinct team dot colors per team index ───
const TEAM_DOT_COLORS = [
  "#27AE60", // green
  "#3498DB", // blue
  "#E67E22", // orange
  "#8E44AD", // purple
  "#E74C3C", // red
  "#1ABC9C", // teal
];

/** Get initials from a full name, e.g. "Martin Novak" -> "MN" */
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface TeamManagementDialogProps {
  open: boolean;
  onClose: () => void;
}

const TeamManagementDialog: React.FC<TeamManagementDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("operations");
  const confirm = useConfirmation();
  const { teams } = useAppSelector((state: RootState) => state.workOrders);
  const { users } = useAppSelector<UserState>(state => state.customerUsers);

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState(false);

  // Create form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Inline edit state for team detail
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescValue, setEditDescValue] = useState("");

  // RC-6: Location assignment state
  const [teamLocations, setTeamLocations] = useState<TeamLocation[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [addingLocation, setAddingLocation] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(fetchTeams());
      dispatch(getUsers());
      // Fetch company locations for the picker
      const fetchLocations = async () => {
        try {
          const res = await api.get<any[]>("/v1/locations", {
            baseURL: (window as any).app.config.customersApiBaseUrl,
          });
          const raw = res.data || [];
          // Flatten hierarchy: parent + children
          const flat: Location[] = [];
          raw.forEach((parent: any) => {
            flat.push(parent);
            (parent.childLocations || []).forEach((child: any) => {
              flat.push({ ...child, parentLocationName: parent.name });
            });
          });
          setAllLocations(flat);
        } catch {
          setAllLocations([]);
        }
      };
      fetchLocations();
    }
  }, [open, dispatch]);

  // Auto-select first team
  useEffect(() => {
    if (teams.length > 0 && !selectedTeamId && !createMode) {
      setSelectedTeamId(teams[0].id);
    }
  }, [teams, selectedTeamId, createMode]);

  // RC-6: Fetch team locations when a team is selected
  useEffect(() => {
    if (!selectedTeamId) {
      setTeamLocations([]);
      return;
    }
    const fetchLocs = async () => {
      try {
        const locs = await teamsApi.getTeamLocations(selectedTeamId);
        setTeamLocations(locs);
      } catch {
        setTeamLocations([]);
      }
    };
    fetchLocs();
  }, [selectedTeamId, teams]); // Re-fetch after teams list refreshes (mutations trigger fetchTeams)

  const selectedTeam = useMemo(
    () => teams.find(t => t.id === selectedTeamId) || null,
    [teams, selectedTeamId]
  );

  const handleCreateTeam = useCallback(async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    try {
      const result = await dispatch(
        createTeam({ name: name.trim(), description: description.trim() || undefined })
      );
      if (createTeam.fulfilled.match(result)) {
        setSelectedTeamId(result.payload.id);
        setCreateMode(false);
        setName("");
        setDescription("");
      }
    } finally {
      await dispatch(fetchTeams());
      setSubmitting(false);
    }
  }, [dispatch, name, description, submitting]);

  const handleDeleteTeam = useCallback(async () => {
    if (!selectedTeamId) return;
    try {
      const result = await dispatch(deleteTeam(selectedTeamId));
      if (deleteTeam.fulfilled.match(result)) {
        setSelectedTeamId(null);
      }
    } finally {
      await dispatch(fetchTeams());
    }
  }, [dispatch, selectedTeamId]);

  const handleAddMember = useCallback(async (user: User) => {
    if (!selectedTeamId || !user) return;
    try {
      const result = await dispatch(
        addTeamMember({
          teamId: selectedTeamId,
          model: {
            userId: user.id,
            userName: `${user.firstName} ${user.lastName}`.trim(),
          },
        })
      );
      if (addTeamMember.fulfilled.match(result)) {
        setAddingMember(false);
      }
    } finally {
      await dispatch(fetchTeams());
    }
  }, [dispatch, selectedTeamId]);

  const handleRemoveMember = useCallback(
    async (userId: string) => {
      if (!selectedTeamId) return;
      try {
        await dispatch(removeTeamMember({ teamId: selectedTeamId, userId }));
      } finally {
        await dispatch(fetchTeams());
      }
    },
    [dispatch, selectedTeamId]
  );

  /** Immediate-save: update a single team field via updateTeam thunk */
  const handleSaveTeamField = useCallback(
    async (field: Partial<{ name: string; description: string; leadUserId: string; leadUserName: string }>) => {
      if (!selectedTeam) return;
      try {
        await dispatch(
          updateTeam({
            id: selectedTeam.id,
            model: {
              name: field.name ?? selectedTeam.name,
              description: field.description !== undefined ? field.description : selectedTeam.description,
              leadUserId: field.leadUserId !== undefined ? field.leadUserId : selectedTeam.leadUserId,
              leadUserName: field.leadUserName !== undefined ? field.leadUserName : selectedTeam.leadUserName,
            },
          })
        );
      } finally {
        await dispatch(fetchTeams());
      }
    },
    [dispatch, selectedTeam]
  );

  const handleSaveName = useCallback(async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed === selectedTeam?.name) {
      setEditingName(false);
      return;
    }
    await handleSaveTeamField({ name: trimmed });
    setEditingName(false);
  }, [editNameValue, selectedTeam, handleSaveTeamField]);

  const handleSaveDescription = useCallback(async () => {
    const trimmed = editDescValue.trim();
    if (trimmed === (selectedTeam?.description || "")) {
      setEditingDescription(false);
      return;
    }
    await handleSaveTeamField({ description: trimmed || undefined });
    setEditingDescription(false);
  }, [editDescValue, selectedTeam, handleSaveTeamField]);

  const handleSetLead = useCallback(
    async (userId: string, userName: string) => {
      await handleSaveTeamField({ leadUserId: userId, leadUserName: userName });
    },
    [handleSaveTeamField]
  );

  // RC-6: Location handlers
  const handleAddLocation = useCallback(
    async (location: Location) => {
      if (!selectedTeamId) return;
      const currentIds = teamLocations.map(l => l.locationId);
      if (currentIds.includes(location.id)) return;
      try {
        await dispatch(
          updateTeamLocations({
            teamId: selectedTeamId,
            locationIds: [...currentIds, location.id],
          })
        );
        setAddingLocation(false);
      } finally {
        // Re-fetch team locations to get enriched data (names)
        try {
          const locs = await teamsApi.getTeamLocations(selectedTeamId);
          setTeamLocations(locs);
        } catch { /* ignore */ }
        await dispatch(fetchTeams());
      }
    },
    [dispatch, selectedTeamId, teamLocations]
  );

  const handleRemoveLocation = useCallback(
    async (locationId: string) => {
      if (!selectedTeamId) return;
      try {
        await dispatch(removeTeamLocation({ teamId: selectedTeamId, locationId }));
      } finally {
        try {
          const locs = await teamsApi.getTeamLocations(selectedTeamId);
          setTeamLocations(locs);
        } catch { /* ignore */ }
        await dispatch(fetchTeams());
      }
    },
    [dispatch, selectedTeamId]
  );

  // Available locations (not already assigned to the team)
  const availableLocations = useMemo(() => {
    const assignedIds = new Set(teamLocations.map(l => l.locationId));
    return allLocations.filter(l => !assignedIds.has(l.id));
  }, [allLocations, teamLocations]);

  const startCreate = useCallback(() => {
    setName("");
    setDescription("");
    setCreateMode(true);
    setSelectedTeamId(null);
  }, []);

  // Members not already on the selected team
  const availableUsers = useMemo(() => {
    const memberIds = new Set(selectedTeam?.members?.map(m => m.userId) || []);
    return users.filter(u => !memberIds.has(u.id));
  }, [users, selectedTeam]);

  /** Compute total WO count for a team from its members */
  const getTeamWoCount = (team: Team): number => {
    if (!team.members || team.members.length === 0) return 0;
    return team.members.reduce((sum, m) => sum + (m.openWorkOrderCount || 0), 0);
  };

  /** Max WO count among members of the selected team (for progress bar scaling) */
  const maxMemberWoCount = useMemo(() => {
    if (!selectedTeam?.members || selectedTeam.members.length === 0) return 1;
    return Math.max(...selectedTeam.members.map(m => m.openWorkOrderCount || 0), 1);
  }, [selectedTeam]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          backgroundColor: dt.cardBg,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: "0 8px 40px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.04)",
          minHeight: "420px",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ display: "flex", p: 0, height: "100%" }}>
        {/* ─── Left: Team sidebar ─── */}
        <TeamSidebar
          teams={teams}
          selectedTeamId={selectedTeamId}
          createMode={createMode}
          onSelectTeam={(id) => {
            setSelectedTeamId(id);
            setCreateMode(false);
            setAddingMember(false);
            setAddingLocation(false);
            setEditingName(false);
            setEditingDescription(false);
          }}
          onCreateClick={startCreate}
          getTeamWoCount={getTeamWoCount}
          translate={translate}
        />

        {/* ─── Right: Detail panel ─── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {createMode ? (
            <CreateTeamPanel
              name={name}
              description={description}
              setName={setName}
              setDescription={setDescription}
              submitting={submitting}
              onCancel={() => {
                setCreateMode(false);
                if (teams.length > 0) setSelectedTeamId(teams[0].id);
              }}
              onSubmit={handleCreateTeam}
              onClose={onClose}
              translate={translate}
            />
          ) : selectedTeam ? (
            <TeamDetailPanel
              team={selectedTeam}
              maxMemberWoCount={maxMemberWoCount}
              addingMember={addingMember}
              availableUsers={availableUsers}
              teamLocations={teamLocations}
              availableLocations={availableLocations}
              addingLocation={addingLocation}
              onClose={onClose}
              onDelete={() =>
                confirm({
                  title: translate("teams.deleteTeam"),
                  text: translate("teams.confirmDelete", { name: selectedTeam.name }),
                  confirmButton: translate("teams.deleteTeamConfirmButton"),
                  closeButton: translate("teams.cancel"),
                  severity: "error",
                  onConfirm: handleDeleteTeam,
                })
              }
              onAddMemberToggle={() => setAddingMember(true)}
              onAddMemberCancel={() => setAddingMember(false)}
              onAddMemberSelect={handleAddMember}
              onRemoveMember={handleRemoveMember}
              onSetLead={handleSetLead}
              onAddLocationToggle={() => setAddingLocation(true)}
              onAddLocationCancel={() => setAddingLocation(false)}
              onAddLocationSelect={handleAddLocation}
              onRemoveLocation={handleRemoveLocation}
              editingName={editingName}
              editNameValue={editNameValue}
              onStartEditName={() => {
                setEditNameValue(selectedTeam.name);
                setEditingName(true);
              }}
              onEditNameChange={setEditNameValue}
              onSaveName={handleSaveName}
              onCancelEditName={() => setEditingName(false)}
              editingDescription={editingDescription}
              editDescValue={editDescValue}
              onStartEditDescription={() => {
                setEditDescValue(selectedTeam.description || "");
                setEditingDescription(true);
              }}
              onEditDescChange={setEditDescValue}
              onSaveDescription={handleSaveDescription}
              onCancelEditDescription={() => setEditingDescription(false)}
              translate={translate}
            />
          ) : (
            <EmptyState onClose={onClose} teams={teams} translate={translate} />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TeamManagementDialog;

// ──────────────────────────────────────────────────────────────
// Sub-components (kept in same file to stay under 300 lines)
// ──────────────────────────────────────────────────────────────

/** Left sidebar with team list */
const TeamSidebar: React.FC<{
  teams: Team[];
  selectedTeamId: string | null;
  createMode: boolean;
  onSelectTeam: (id: string) => void;
  onCreateClick: () => void;
  getTeamWoCount: (team: Team) => number;
  translate: (key: string, options?: Record<string, unknown>) => string;
}> = ({ teams, selectedTeamId, createMode, onSelectTeam, onCreateClick, getTeamWoCount, translate }) => (
  <Box
    sx={{
      width: "220px",
      flexShrink: 0,
      borderRight: `1px solid ${dt.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#FAFBFC",
    }}
  >
    {/* Sidebar header */}
    <Box
      sx={{
        padding: "20px 18px 14px",
        fontFamily: dt.font,
        fontSize: "13px",
        fontWeight: 700,
        color: dt.textPrimary,
        letterSpacing: "0.2px",
      }}
    >
      {translate("teams.title")}
    </Box>

    {/* Team list */}
    <Box sx={{ flex: 1, overflowY: "auto", px: "10px", pb: 1 }}>
      {teams.map((t, idx) => {
        const isSelected = t.id === selectedTeamId && !createMode;
        const dotColor = TEAM_DOT_COLORS[idx % TEAM_DOT_COLORS.length];
        const woCount = getTeamWoCount(t);
        return (
          <Box
            key={t.id}
            onClick={() => onSelectTeam(t.id)}
            sx={{
              padding: "10px 12px",
              borderRadius: dt.radiusSm,
              backgroundColor: isSelected ? dt.blueSoft : "transparent",
              cursor: "pointer",
              transition: "all 0.15s ease",
              mb: "2px",
              "&:hover": {
                backgroundColor: isSelected ? dt.blueSoft : dt.rowHover,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* Color dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: dotColor,
                  flexShrink: 0,
                }}
              />
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: dt.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t.name}
              </Box>
            </Box>
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                color: dt.textMuted,
                mt: "3px",
                pl: "16px", // Align with text after dot
              }}
            >
              {translate("teams.memberCount", { count: t.memberCount })} &middot; {woCount} {translate("teams.wos")}
              {(t.locations?.length ?? 0) > 0 && (
                <> &middot; {t.locations!.length} <PlaceOutlined sx={{ fontSize: 10, verticalAlign: "middle", ml: "-1px" }} /></>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>

    {/* Create team button */}
    <Box sx={{ p: "12px 14px", borderTop: `1px solid ${dt.cardBorder}` }}>
      <Button
        onClick={onCreateClick}
        startIcon={
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: `1.5px solid ${dt.blue}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AddOutlined sx={{ fontSize: 14, color: dt.blue }} />
          </Box>
        }
        sx={{
          fontFamily: dt.font,
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "none",
          color: dt.blue,
          justifyContent: "flex-start",
          width: "100%",
          px: 1,
        }}
      >
        {translate("teams.createTeam")}
      </Button>
    </Box>
  </Box>
);

/** Right panel when viewing a team */
const TeamDetailPanel: React.FC<{
  team: Team;
  maxMemberWoCount: number;
  addingMember: boolean;
  availableUsers: User[];
  teamLocations: TeamLocation[];
  availableLocations: Location[];
  addingLocation: boolean;
  onClose: () => void;
  onDelete: () => void;
  onAddMemberToggle: () => void;
  onAddMemberCancel: () => void;
  onAddMemberSelect: (user: User) => void;
  onRemoveMember: (userId: string) => void;
  onSetLead: (userId: string, userName: string) => void;
  onAddLocationToggle: () => void;
  onAddLocationCancel: () => void;
  onAddLocationSelect: (location: Location) => void;
  onRemoveLocation: (locationId: string) => void;
  editingName: boolean;
  editNameValue: string;
  onStartEditName: () => void;
  onEditNameChange: (v: string) => void;
  onSaveName: () => void;
  onCancelEditName: () => void;
  editingDescription: boolean;
  editDescValue: string;
  onStartEditDescription: () => void;
  onEditDescChange: (v: string) => void;
  onSaveDescription: () => void;
  onCancelEditDescription: () => void;
  translate: (key: string) => string;
}> = ({
  team, maxMemberWoCount, addingMember, availableUsers,
  teamLocations, availableLocations, addingLocation,
  onClose, onDelete, onAddMemberToggle, onAddMemberCancel, onAddMemberSelect,
  onRemoveMember, onSetLead,
  onAddLocationToggle, onAddLocationCancel, onAddLocationSelect, onRemoveLocation,
  editingName, editNameValue, onStartEditName, onEditNameChange, onSaveName, onCancelEditName,
  editingDescription, editDescValue, onStartEditDescription, onEditDescChange, onSaveDescription, onCancelEditDescription,
  translate,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "20px 24px 16px",
        borderBottom: `1px solid ${dt.cardBorder}`,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
        {/* Editable team name */}
        {editingName ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <input
              autoFocus
              value={editNameValue}
              onChange={e => onEditNameChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") onSaveName();
                if (e.key === "Escape") onCancelEditName();
              }}
              onBlur={onSaveName}
              style={{
                fontFamily: dt.font,
                fontSize: "18px",
                fontWeight: 700,
                color: dt.textPrimary,
                letterSpacing: "-0.3px",
                border: "none",
                borderBottom: `2px solid ${dt.blue}`,
                outline: "none",
                background: "transparent",
                padding: "0 0 2px",
                width: "100%",
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              "&:hover .edit-name-btn": { opacity: 1 },
            }}
          >
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "18px",
                fontWeight: 700,
                color: dt.textPrimary,
                letterSpacing: "-0.3px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {team.name}
            </Box>
            <IconButton
              className="edit-name-btn"
              size="small"
              onClick={onStartEditName}
              sx={{
                opacity: 0,
                transition: "opacity 0.15s",
                p: "2px",
                color: dt.textMuted,
                "&:hover": { color: dt.blue },
              }}
            >
              <EditOutlined sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        )}

        {/* Editable description */}
        {editingDescription ? (
          <Box sx={{ mt: "4px" }}>
            <input
              autoFocus
              value={editDescValue}
              onChange={e => onEditDescChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") onSaveDescription();
                if (e.key === "Escape") onCancelEditDescription();
              }}
              onBlur={onSaveDescription}
              placeholder={translate("teams.addDescription")}
              style={{
                fontFamily: dt.font,
                fontSize: "13px",
                color: dt.textSecondary,
                border: "none",
                borderBottom: `1.5px solid ${dt.blue}`,
                outline: "none",
                background: "transparent",
                padding: "0 0 2px",
                width: "100%",
              }}
            />
          </Box>
        ) : (
          <Box
            onClick={onStartEditDescription}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: team.description ? dt.textSecondary : dt.textMuted,
              mt: "2px",
              cursor: "pointer",
              fontStyle: team.description ? "normal" : "italic",
              "&:hover": { color: dt.blue },
              transition: "color 0.15s",
            }}
          >
            {team.description || translate("teams.addDescription")}
          </Box>
        )}
      </Box>
      <IconButton onClick={onClose} size="small" sx={{ mt: "-4px", mr: "-4px", flexShrink: 0 }}>
        <CloseOutlined sx={{ fontSize: 20, color: dt.textMuted }} />
      </IconButton>
    </Box>

    {/* Members list */}
    <Box sx={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      {team.members?.map((m, idx) => {
        const color = MEMBER_COLORS[idx % MEMBER_COLORS.length];
        const woCount = m.openWorkOrderCount || 0;
        const barWidth = maxMemberWoCount > 0
          ? Math.max((woCount / maxMemberWoCount) * 100, 6)
          : 6;
        const isLead = team.leadUserId === m.userId;

        return (
          <Box
            key={m.userId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              mb: "16px",
              "&:hover .member-action": { opacity: 1 },
            }}
          >
            {/* Avatar circle */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#FFFFFF",
                  letterSpacing: "0.5px",
                }}
              >
                {getInitials(m.userName)}
              </Box>
            </Box>

            {/* Name + progress bar */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "5px" }}>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: dt.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.userName}
                </Box>
                {isLead ? (
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "9px",
                      fontWeight: 700,
                      color: "#2980B9",
                      backgroundColor: "rgba(52, 152, 219, 0.10)",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      letterSpacing: "0.8px",
                      flexShrink: 0,
                    }}
                  >
                    {translate("teams.lead")}
                  </Box>
                ) : (
                  <Box
                    className="member-action"
                    onClick={() => onSetLead(m.userId, m.userName)}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.15s",
                      fontFamily: dt.font,
                      fontSize: "9px",
                      fontWeight: 600,
                      color: dt.textMuted,
                      borderRadius: "4px",
                      padding: "2px 6px",
                      cursor: "pointer",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      "&:hover": { color: "#2980B9", backgroundColor: "rgba(52, 152, 219, 0.06)" },
                    }}
                  >
                    <StarOutlined sx={{ fontSize: 10 }} />
                    {translate("teams.setLead")}
                  </Box>
                )}
                {/* Remove button (on hover) */}
                <IconButton
                  className="member-action"
                  size="small"
                  onClick={() => onRemoveMember(m.userId)}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.15s",
                    ml: "auto",
                    p: "2px",
                    color: dt.textMuted,
                    "&:hover": { color: "#E74C3C" },
                  }}
                >
                  <CloseOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              {/* Progress bar */}
              <Box
                sx={{
                  height: "5px",
                  backgroundColor: "#F0F2F7",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${barWidth}%`,
                    height: "100%",
                    backgroundColor: color,
                    borderRadius: "3px",
                    transition: "width 0.4s ease",
                  }}
                />
              </Box>
            </Box>

            {/* WO count */}
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "12px",
                fontWeight: 500,
                color: dt.textMuted,
                flexShrink: 0,
                minWidth: "50px",
                textAlign: "right",
              }}
            >
              {woCount} {translate("teams.wos")}
            </Box>
          </Box>
        );
      })}

      {/* Add member row */}
      {addingMember ? (
        <Box sx={{ display: "flex", alignItems: "center", mt: "8px", pl: "50px" }}>
          <Autocomplete
            options={availableUsers}
            value={null}
            openOnFocus
            autoFocus
            getOptionLabel={u => `${u.firstName} ${u.lastName}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, val) => {
              if (val) onAddMemberSelect(val);
            }}
            onBlur={onAddMemberCancel}
            sx={{ flex: 1 }}
            renderInput={params => (
              <TextField
                {...params}
                autoFocus
                placeholder={translate("fields.selectUser")}
                size="small"
                sx={textFieldSx}
              />
            )}
          />
        </Box>
      ) : (
        <Box
          onClick={onAddMemberToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            cursor: "pointer",
            mt: "4px",
            "&:hover .add-circle": {
              borderColor: dt.blue,
              backgroundColor: dt.blueSoft,
            },
          }}
        >
          {/* Dashed circle */}
          <Box
            className="add-circle"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: `1.5px dashed ${dt.cardBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.15s ease",
            }}
          >
            <AddOutlined sx={{ fontSize: 16, color: dt.textMuted }} />
          </Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textMuted,
            }}
          >
            {translate("teams.addMember")}
          </Box>
        </Box>
      )}

      {/* ─── RC-6: Assigned Locations section ─── */}
      <Box sx={{ mt: "24px", pt: "16px", borderTop: `1px solid ${dt.cardBorder}` }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 700,
              color: dt.textMuted,
              letterSpacing: "0.8px",
              textTransform: "uppercase",
            }}
          >
            <PlaceOutlined sx={{ fontSize: 14, color: dt.textMuted }} />
            {translate("teams.assignedLocations")}
          </Box>
        </Box>

        {teamLocations.length === 0 && !addingLocation && (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: dt.textMuted,
              fontStyle: "italic",
              mb: "8px",
              pl: "2px",
            }}
          >
            {translate("teams.noLocations")}
            <Box
              component="span"
              sx={{ display: "block", fontSize: "11px", mt: "2px" }}
            >
              {translate("teams.noLocationsHint")}
            </Box>
          </Box>
        )}

        {teamLocations.map(loc => (
          <Box
            key={loc.locationId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              mb: "8px",
              padding: "6px 8px",
              borderRadius: dt.radiusSm,
              backgroundColor: "#F6F8FC",
              "&:hover .loc-remove": { opacity: 1 },
            }}
          >
            <PlaceOutlined sx={{ fontSize: 16, color: dt.blue, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: dt.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {loc.locationName}
              </Box>
              {loc.parentLocationName && (
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    color: dt.textMuted,
                  }}
                >
                  {loc.parentLocationName}
                </Box>
              )}
            </Box>
            <IconButton
              className="loc-remove"
              size="small"
              onClick={() => onRemoveLocation(loc.locationId)}
              sx={{
                opacity: 0,
                transition: "opacity 0.15s",
                p: "2px",
                color: dt.textMuted,
                "&:hover": { color: "#E74C3C" },
              }}
            >
              <CloseOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {/* Add location */}
        {addingLocation ? (
          <Box sx={{ mt: "8px" }}>
            <Autocomplete
              options={availableLocations}
              value={null}
              openOnFocus
              autoFocus
              getOptionLabel={loc =>
                loc.parentLocationName
                  ? `${loc.name} (${loc.parentLocationName})`
                  : loc.name
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, val) => {
                if (val) onAddLocationSelect(val);
              }}
              onBlur={onAddLocationCancel}
              sx={{ flex: 1 }}
              renderInput={params => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder={translate("teams.addLocation")}
                  size="small"
                  sx={textFieldSx}
                />
              )}
            />
          </Box>
        ) : (
          <Box
            onClick={onAddLocationToggle}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              mt: "4px",
              "&:hover .add-loc-circle": {
                borderColor: dt.blue,
                backgroundColor: dt.blueSoft,
              },
            }}
          >
            <Box
              className="add-loc-circle"
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: `1.5px dashed ${dt.cardBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all 0.15s ease",
              }}
            >
              <AddOutlined sx={{ fontSize: 14, color: dt.textMuted }} />
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: dt.textMuted,
              }}
            >
              {translate("teams.addLocation")}
            </Box>
          </Box>
        )}
      </Box>
    </Box>

    {/* Footer — only Delete Team (no Save/Cancel since we use immediate-save) */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        padding: "14px 24px",
        borderTop: `1px solid ${dt.cardBorder}`,
      }}
    >
      <Button
        onClick={onDelete}
        size="small"
        sx={{
          fontFamily: dt.font,
          fontSize: "12px",
          fontWeight: 500,
          textTransform: "none",
          color: "#E74C3C",
          "&:hover": { backgroundColor: "rgba(231,76,60,0.06)" },
        }}
      >
        {translate("teams.deleteTeam")}
      </Button>
    </Box>
  </Box>
);

/** Right panel when creating a new team */
const CreateTeamPanel: React.FC<{
  name: string;
  description: string;
  setName: (v: string) => void;
  setDescription: (v: string) => void;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onClose: () => void;
  translate: (key: string) => string;
}> = ({ name, description, setName, setDescription, submitting, onCancel, onSubmit, onClose, translate }) => (
  <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "20px 24px 16px",
        borderBottom: `1px solid ${dt.cardBorder}`,
      }}
    >
      <Box>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "18px",
            fontWeight: 700,
            color: dt.textPrimary,
            letterSpacing: "-0.3px",
          }}
        >
          {translate("teams.createTeam")}
        </Box>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            color: dt.textSecondary,
            mt: "2px",
          }}
        >
          {translate("teams.manageWorkload")}
        </Box>
      </Box>
      <IconButton onClick={onClose} size="small" sx={{ mt: "-4px", mr: "-4px" }}>
        <CloseOutlined sx={{ fontSize: 20, color: dt.textMuted }} />
      </IconButton>
    </Box>

    {/* Form */}
    <Box sx={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 2 }}>
      <FormTextField
        value={name}
        onChange={setName}
        placeholder={translate("teams.teamName")}
      />
      <FormTextarea
        value={description}
        onChange={setDescription}
        rows={2}
        minHeight="50px"
      />
    </Box>

    {/* Footer */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        padding: "14px 24px",
        borderTop: `1px solid ${dt.cardBorder}`,
      }}
    >
      <Button
        onClick={onCancel}
        size="small"
        sx={{
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 500,
          textTransform: "none",
          color: dt.textSecondary,
          borderRadius: dt.radiusSm,
          px: 2,
        }}
      >
        {translate("teams.cancel")}
      </Button>
      <Button
        onClick={onSubmit}
        disabled={!name.trim() || submitting}
        size="small"
        sx={{
          background: accentGradientDark,
          color: "#fff",
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 600,
          textTransform: "none",
          borderRadius: dt.radiusSm,
          px: 3,
          "&:hover": { background: accentGradientDarkHover },
          "&:disabled": { opacity: 0.5 },
        }}
      >
        {translate("teams.createTeam")}
      </Button>
    </Box>
  </Box>
);

/** Empty state when no team is selected */
const EmptyState: React.FC<{
  onClose: () => void;
  teams: Team[];
  translate: (key: string) => string;
}> = ({ onClose, teams, translate }) => (
  <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        padding: "20px 24px 0",
      }}
    >
      <IconButton onClick={onClose} size="small">
        <CloseOutlined sx={{ fontSize: 20, color: dt.textMuted }} />
      </IconButton>
    </Box>
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: dt.font,
        fontSize: "14px",
        color: dt.textMuted,
      }}
    >
      {teams.length === 0
        ? translate("teams.noTeams")
        : translate("teams.title")}
    </Box>
  </Box>
);
